import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";
import { useNavigate } from "react-router-dom";
import { passkeyService } from "../services/passkey.service";
import { useAuthStore } from "../store";
import { setAuthToken } from "../lib";

export function usePasskeys() {
  return useQuery({
    queryKey: ["passkeys"],
    queryFn: () => passkeyService.listPasskeys(),
  });
}

export function useRegisterPasskey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceName: string) => {
      const options = await passkeyService.getRegistrationOptions();
      const credential = await startRegistration({ optionsJSON: options });
      return passkeyService.verifyRegistration(credential, deviceName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["passkeys"] });
      notifications.show({
        title: "Passkey Registered",
        message: "Your passkey has been registered successfully.",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.name === "NotAllowedError"
          ? "Passkey registration was cancelled."
          : error?.response?.data?.message || "Failed to register passkey.";
      notifications.show({
        title: "Registration Failed",
        message,
        color: "red",
      });
    },
  });
}

export function useRemovePasskey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => passkeyService.removePasskey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["passkeys"] });
      notifications.show({
        title: "Passkey Removed",
        message: "Your passkey has been removed.",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.message || "Failed to remove passkey.",
        color: "red",
      });
    },
  });
}

export function usePasskeyLogin() {
  const { login } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (username?: string) => {
      const response = await passkeyService.getLoginOptions(username);
      const { challengeKey, ...optionsJSON } = response;
      const credential = await startAuthentication({ optionsJSON });
      return passkeyService.verifyLogin(username, credential, challengeKey);
    },
    onSuccess: (response) => {
      setAuthToken(response.token);
      login(response.token, {
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        fullName: response.user.fullName,
        roleId: response.user.roleId || "",
        roleName: response.user.roleName,
        groups: response.user.groups || [],
        groupId: response.user.groupId || undefined,
        groupName: response.user.groupName,
        facilityId: response.user.facilityId || undefined,
        facilityName: response.user.facilityName,
        isActive: response.user.isActive,
        isRemote: response.user.isRemote,
      });

      const pathologistGuardEnabled =
        process.env.REACT_APP_ENABLE_PATHOLOGIST_GUARD !== "false";
      navigate(
        response.user.roleName === "Pathologist" && pathologistGuardEnabled
          ? "/pathologist-review"
          : "/dashboard"
      );
    },
    onError: (error: any) => {
      const message =
        error?.name === "NotAllowedError"
          ? "Passkey authentication was cancelled."
          : error?.response?.data?.message ||
            "Passkey authentication failed. Try using your password.";
      notifications.show({
        title: "Passkey Login Failed",
        message,
        color: "red",
      });
    },
  });
}
